/**
 * API-Football group tables for WC-26.
 *
 * Path: `competitions/{cid}/groupStandings/current` (public read; Cloud Functions write).
 */
import type { Country } from './country';
import type { WorldCupGroupId } from './wc26Groups';

/** Compatible with client and admin Firestore Timestamp. */
export type GroupStandingsTimestamp = {
  seconds: number;
  nanoseconds: number;
  toMillis(): number;
  toDate(): Date;
};

export type GroupStandingRow = {
  country: Country;
  pts: number;
  mp: number;
  gd: number;
  gf: number;
  ga: number;
  w: number;
  d: number;
  l: number;
};

export type GroupStandingsSnapshot = {
  groups: Partial<Record<WorldCupGroupId, GroupStandingRow[]>>;
  lastUpdated: GroupStandingsTimestamp;
  source: 'api-football';
};

export function emptyStanding(country: Country): GroupStandingRow {
  return { country, pts: 0, mp: 0, gd: 0, gf: 0, ga: 0, w: 0, d: 0, l: 0 };
}

/** Zeroed rows in roster order when API data is unavailable. */
export function emptyStandingsForRoster(teams: Country[]): GroupStandingRow[] {
  return teams.map(emptyStanding);
}

/** Keep API rank order; append roster teams missing from the sync (unmapped API names). */
export function mergeStandingsWithRoster(
  standings: GroupStandingRow[],
  roster: Country[],
): GroupStandingRow[] {
  if (standings.length === 0) {
    return emptyStandingsForRoster(roster);
  }
  const present = new Set(standings.map((row) => row.country));
  const missing = roster.filter((country) => !present.has(country));
  if (missing.length === 0) {
    return standings;
  }
  return [...standings, ...missing.map(emptyStanding)];
}
